import { useState, useRef, useEffect } from "react";
import {
  FileText,
  Mic,
  Video,
  Paperclip,
  Square,
  Trash2,
  Upload,
  Circle,
  Pause,
  Play,
  Shield,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useI18n } from "@/i18n";

const MAX_BYTES = 500 * 1024 * 1024;           // 500MB hard cap
const INLINE_BASE64_LIMIT = 4 * 1024 * 1024;   // < 4MB → stored inline as base64
const MAX_AUDIO_SECONDS = 30 * 60;             // 30 min cap on in-app recording
const PRIVACY_KEY = "ks_media_privacy_v1";

const TAB_DEFS = [
  { k: "text", icon: FileText, labelKey: "media_kind_text" },
  { k: "audio", icon: Mic, labelKey: "media_kind_audio" },
  { k: "video", icon: Video, labelKey: "media_kind_video" },
  { k: "file", icon: Paperclip, labelKey: "media_kind_file" },
];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function fmtBytes(b) {
  if (!b && b !== 0) return "—";
  if (b > 1024 * 1024 * 1024) return `${(b / 1073741824).toFixed(2)} GB`;
  if (b > 1024 * 1024) return `${(b / 1048576).toFixed(1)} MB`;
  return `${Math.round(b / 1024)} KB`;
}
function fmtTime(s) {
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
}

// Reads the video's intrinsic resolution & duration without re-encoding.
// Returns { ok, warn } — never rejects on low resolution (user picked the file knowingly),
// only on impractical duration so the upload doesn't get stuck.
function validateVideoQuality(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    v.playsInline = true;
    v.src = url;
    const cleanup = () => URL.revokeObjectURL(url);
    v.onloadedmetadata = () => {
      const w = v.videoWidth;
      const h = v.videoHeight;
      const dur = v.duration;
      cleanup();
      if (dur && dur > 60 * 60) {
        toast.error(t("media_video_too_long"));
        resolve(false);
        return;
      }
      if (w && h) {
        const minSide = Math.min(w, h);
        if (minSide < 480) {
          toast.warning(t("media_video_low_res").replace("{W}", w).replace("{H}", h));
        }
      }
      resolve(true);
    };
    v.onerror = () => { cleanup(); resolve(true); }; // tolerant fallback
  });
}

function PrivacyGate({ onAccept, onCancel }) {
  const [ack, setAck] = useState(false);
  return (
    <div className="bg-obsidian-2/90 border border-gold-subtle rounded-md p-5 mb-3" data-testid="media-privacy-gate">
      <div className="flex items-start gap-3 mb-3">
        <Shield className="w-5 h-5 text-gold shrink-0 mt-0.5" />
        <div>
          <div className="text-gold-light text-sm mb-2">Tus archivos están protegidos</div>
          <p className="text-xs text-muted leading-relaxed">
            🔒 Tus archivos están cifrados con <strong className="text-gold-light">AES-256</strong> antes de ser almacenados.
            Solo tú y las personas que designes pueden acceder a ellos. KeepSoul nunca lee ni comparte tu contenido.
          </p>
          <p className="text-xs text-faded mt-2 leading-relaxed">
            ✓ Cifrado en servidores europeos &nbsp; ✓ Puedes eliminarlos en cualquier momento &nbsp; ✓ Cumple GDPR
          </p>
        </div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer text-sm mb-3">
        <input
          type="checkbox"
          checked={ack}
          onChange={(e) => setAck(e.target.checked)}
          className="accent-yellow-600"
          data-testid="media-privacy-ack"
        />
        <span className="text-gold-light">Entendido</span>
      </label>
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="btn-ghost flex-1 justify-center text-xs">
          Cancelar
        </button>
        <button
          type="button"
          disabled={!ack}
          onClick={onAccept}
          className="btn-gold flex-1 justify-center text-xs disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="media-privacy-continue"
        >
          Continuar
        </button>
      </div>
    </div>
  );
}

export default function MediaInput({
  textValue,
  onTextChange,
  media,
  onMediaChange,
  textLabel = "Texto",
  textRequired = false,
  textPlaceholder = "",
  textRows = 5,
}) {
  const { t } = useI18n();
  const TABS = TAB_DEFS.map((td) => ({ ...td, label: t(td.labelKey) }));
  const initialTab = media?.media_type && media.media_type !== "text" ? media.media_type : "text";
  const [tab, setTab] = useState(initialTab);

  // Privacy gate
  const [showGate, setShowGate] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [uploadPct, setUploadPct] = useState(null);

  // Audio recording state
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [recordTime, setRecordTime] = useState(0);
  const [pendingBlob, setPendingBlob] = useState(null);   // for preview before saving
  const [pendingUrl, setPendingUrl] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const animRef = useRef(null);
  const [levels, setLevels] = useState(Array(24).fill(0));

  useEffect(() => {
    return () => cleanupRecording();
  // eslint-disable-next-line
  }, []);

  function cleanupRecording() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (animRef.current) {
      cancelAnimationFrame(animRef.current);
      animRef.current = null;
    }
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      try { audioCtxRef.current.close(); } catch (_) {}
      audioCtxRef.current = null;
    }
  }

  function withPrivacy(action) {
    const accepted = localStorage.getItem(PRIVACY_KEY);
    if (accepted) {
      action();
      return;
    }
    setPendingAction(() => action);
    setShowGate(true);
  }

  function acceptPrivacy() {
    localStorage.setItem(PRIVACY_KEY, "1");
    setShowGate(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  }

  // ============ AUDIO RECORDING ============
  async function startAudioRecording() {
    try {
      // Request mic. On iOS Safari this MUST be triggered by a user gesture (the button click is one).
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Pick best supported mime type for audio
      const mimeCandidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/aac",
        "audio/mpeg",
      ];
      const mimeType = mimeCandidates.find((m) =>
        typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)
      ) || "";

      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        const url = URL.createObjectURL(blob);
        setPendingBlob(blob);
        setPendingUrl(url);
        cleanupRecording();
        setRecording(false);
        setPaused(false);
      };

      // Set up audio analyser for waveform
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx();
        audioCtxRef.current = ctx;
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 64;
        src.connect(analyser);
        analyserRef.current = analyser;
        const data = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          analyser.getByteFrequencyData(data);
          const stride = Math.max(1, Math.floor(data.length / 24));
          const next = [];
          for (let i = 0; i < 24; i++) {
            next.push(data[i * stride] || 0);
          }
          setLevels(next);
          animRef.current = requestAnimationFrame(tick);
        };
        tick();
      } catch (_) { /* AudioContext optional */ }

      mr.start(250);
      mediaRecorderRef.current = mr;
      setRecording(true);
      setPaused(false);
      setRecordTime(0);
      timerRef.current = setInterval(() => {
        setRecordTime((s) => {
          if (s + 1 >= MAX_AUDIO_SECONDS) {
            stopAudioRecording();
            toast.warning(t("media_audio_limit_30min"));
            return MAX_AUDIO_SECONDS;
          }
          return s + 1;
        });
      }, 1000);
    } catch (e) {
      console.error(e);
      const denied = (e && e.name === "NotAllowedError") || /denied|permission/i.test(String(e?.message));
      toast.error(
        denied
          ? t("media_mic_denied")
          : t("media_mic_failed")
      );
    }
  }

  function pauseAudioRecording() {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.pause();
      setPaused(true);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }
  function resumeAudioRecording() {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "paused") {
      mediaRecorderRef.current.resume();
      setPaused(false);
      timerRef.current = setInterval(() => setRecordTime((s) => s + 1), 1000);
    }
  }
  function stopAudioRecording() {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setRecording(false);
  }
  function discardPending() {
    if (pendingUrl) URL.revokeObjectURL(pendingUrl);
    setPendingBlob(null);
    setPendingUrl(null);
    setRecordTime(0);
  }
  async function savePending() {
    if (!pendingBlob) return;
    const filename = `audio_${Date.now()}.${pendingBlob.type.includes("mp4") ? "m4a" : "webm"}`;
    const file = new File([pendingBlob], filename, { type: pendingBlob.type });
    await persistFile(file, "audio");
    discardPending();
  }

  // ============ FILE UPLOAD ============
  async function persistFile(file, forceType) {
    if (file.size > MAX_BYTES) {
      toast.error(t("media_file_too_large").replace("{MB}", String(MAX_BYTES / 1048576)));
      return;
    }
    const mt =
      forceType ||
      (file.type.startsWith("image/") ? "image" :
       file.type.startsWith("audio/") ? "audio" :
       file.type.startsWith("video/") ? "video" : "file");

    // STRICT VIDEO CONSTRAINTS — verify resolution & duration on client BEFORE upload.
    // We don't re-encode; we just reject sub-HD or absurdly-long clips to ensure quality.
    if (mt === "video") {
      const accepted = await validateVideoQuality(file);
      if (!accepted) return;
    }

    try {
      if (file.size <= INLINE_BASE64_LIMIT) {
        // Small file → keep inline base64 (works during preview without server roundtrip)
        const dataUrl = await fileToBase64(file);
        onMediaChange({
          media_type: mt,
          media_data: dataUrl,
          media_filename: file.name,
          media_size: file.size,
          media_id: "",
        });
        toast.success(t("media_file_added"));
        return;
      }

      // Large file → upload to GridFS with progress
      setUploadPct(0);
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadPct(Math.round((evt.loaded / evt.total) * 100));
        },
        timeout: 300000,
      });
      onMediaChange({
        media_type: mt,
        media_data: "",
        media_filename: r.data.filename,
        media_size: r.data.size,
        media_id: r.data.media_id,
      });
      toast.success(t("media_file_uploaded").replace("{NAME}", file.name));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("media_upload_failed"));
    } finally {
      setUploadPct(null);
    }
  }

  function handleFileInput(e, forceType) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    withPrivacy(() => persistFile(file, forceType));
  }

  function clearMedia() {
    onMediaChange({ media_type: "text", media_data: "", media_filename: "", media_size: 0, media_id: "" });
  }

  const hasMedia = media?.media_data || media?.media_id;
  // Resolve playback url for previously-saved media
  const mediaUrl = media?.media_id
    ? `${API}/media/${media.media_id}`
    : media?.media_data || null;

  return (
    <div className="space-y-3" data-testid="media-input">
      <div className="flex gap-1 border-b border-gold-subtle overflow-x-auto">
        {TABS.map((tb) => {
          const Icon = tb.icon;
          const active = tab === tb.k;
          return (
            <button
              key={tb.k}
              type="button"
              onClick={() => {
                if (recording) stopAudioRecording();
                setTab(tb.k);
              }}
              className={`shrink-0 flex items-center gap-2 px-3 py-2 text-xs font-label transition border-b-2 -mb-px ${
                active ? "text-gold border-gold" : "text-faded border-transparent hover:text-muted"
              }`}
              data-testid={`media-tab-${tb.k}`}
            >
              <Icon className="w-3.5 h-3.5" /> {tb.label}
            </button>
          );
        })}
      </div>

      {showGate && <PrivacyGate onAccept={acceptPrivacy} onCancel={() => { setShowGate(false); setPendingAction(null); }} />}

      {tab === "text" && (
        <div>
          <label className="font-label text-faded block mb-2">{textLabel}</label>
          <textarea
            className="input-field"
            rows={textRows}
            required={textRequired}
            value={textValue || ""}
            onChange={(e) => onTextChange(e.target.value)}
            placeholder={textPlaceholder}
            data-testid="media-text-input"
            autoComplete="off"
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
          />
        </div>
      )}

      {/* ============ AUDIO TAB ============ */}
      {tab === "audio" && (
        <div>
          {hasMedia && media.media_type === "audio" ? (
            <div className="glass-card rounded-md p-4 space-y-3">
              <audio controls src={mediaUrl} className="w-full" preload="metadata" />
              <div className="flex items-center justify-between text-xs text-faded">
                <span className="truncate flex-1">{media.media_filename} · {fmtBytes(media.media_size)}</span>
                <button type="button" onClick={clearMedia} className="text-faded hover:text-red-400 flex items-center gap-1">
                  <Trash2 className="w-3.5 h-3.5" /> Quitar
                </button>
              </div>
            </div>
          ) : pendingBlob ? (
            // Playback preview before save
            <div className="glass-card rounded-md p-4 space-y-3">
              <div className="text-xs text-gold-light flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-gold" /> Escucha tu grabación antes de guardar
              </div>
              <audio controls src={pendingUrl} className="w-full" />
              <div className="text-xs text-faded">{fmtTime(recordTime)} · {fmtBytes(pendingBlob.size)}</div>
              <div className="flex gap-2">
                <button type="button" onClick={discardPending} className="btn-ghost flex-1 justify-center text-xs">
                  <Trash2 className="w-3 h-3" /> Descartar
                </button>
                <button type="button" onClick={savePending} className="btn-gold flex-1 justify-center text-xs" data-testid="media-save-audio">
                  <CheckCircle2 className="w-3 h-3" /> Guardar
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {recording && (
                <div className="glass-card rounded-md p-4 bg-red-900/10 border-red-900/30">
                  <div className="flex items-end gap-1 h-12 mb-3 justify-center">
                    {levels.map((v, i) => (
                      <div
                        key={i}
                        className="bg-gold rounded-full transition-all duration-75"
                        style={{
                          width: 3,
                          height: `${Math.max(4, (v / 255) * 48)}px`,
                          opacity: paused ? 0.3 : 0.8,
                        }}
                      />
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full bg-red-500 ${paused ? "" : "animate-pulse"}`}></span>
                      <span className="text-sm font-mono text-gold-light">{fmtTime(recordTime)}</span>
                      <span className="text-[10px] text-faded">/ {fmtTime(MAX_AUDIO_SECONDS)}</span>
                    </div>
                    <div className="flex gap-2">
                      {paused ? (
                        <button type="button" onClick={resumeAudioRecording} className="btn-ghost px-2 py-1 text-xs">
                          <Play className="w-3 h-3" /> Continuar
                        </button>
                      ) : (
                        <button type="button" onClick={pauseAudioRecording} className="btn-ghost px-2 py-1 text-xs">
                          <Pause className="w-3 h-3" /> Pausar
                        </button>
                      )}
                      <button type="button" onClick={stopAudioRecording} className="btn-gold px-2 py-1 text-xs" data-testid="media-stop-rec">
                        <Square className="w-3 h-3" /> Detener
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {!recording && (
                <>
                  <button
                    type="button"
                    onClick={() => withPrivacy(startAudioRecording)}
                    className="btn-gold w-full justify-center"
                    data-testid="media-start-audio"
                  >
                    <Circle className="w-4 h-4 fill-current text-red-300" /> Grabar audio
                  </button>
                  <p className="text-[11px] text-faded text-center">
                    Pulsa para grabar. Te pediremos permiso de micrófono. Máx 30 min.
                  </p>
                  <label className="btn-ghost w-full justify-center cursor-pointer">
                    <Upload className="w-4 h-4" /> O subir audio existente
                    <input type="file" accept="audio/*" hidden onChange={(e) => handleFileInput(e, "audio")} data-testid="media-upload-audio" />
                  </label>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ============ VIDEO TAB (upload-only) ============ */}
      {tab === "video" && (
        <div>
          {hasMedia && media.media_type === "video" ? (
            <div className="glass-card rounded-md p-4 space-y-3">
              <video controls src={mediaUrl} className="w-full rounded-md bg-black" preload="metadata" style={{ maxHeight: 360 }} />
              <div className="flex items-center justify-between text-xs text-faded">
                <span className="truncate flex-1">{media.media_filename} · {fmtBytes(media.media_size)}</span>
                <button type="button" onClick={clearMedia} className="text-faded hover:text-red-400 flex items-center gap-1">
                  <Trash2 className="w-3.5 h-3.5" /> Quitar
                </button>
              </div>
            </div>
          ) : uploadPct !== null ? (
            <div className="glass-card rounded-md p-6 text-center">
              <Loader2 className="w-6 h-6 text-gold animate-spin mx-auto mb-3" />
              <div className="text-sm text-gold-light mb-2">Subiendo… {uploadPct}%</div>
              <div className="h-1.5 bg-obsidian-2 rounded overflow-hidden">
                <div className="h-full bg-gold transition-all duration-150" style={{ width: `${uploadPct}%` }} />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="btn-gold w-full justify-center cursor-pointer text-sm py-3" data-testid="media-record-video-label">
                <Video className="w-4 h-4" /> Grabar vídeo con la cámara
                <input
                  type="file"
                  accept="video/*"
                  capture="environment"
                  hidden
                  onChange={(e) => handleFileInput(e, "video")}
                  data-testid="media-record-video"
                />
              </label>
              <label className="btn-ghost w-full justify-center cursor-pointer text-sm py-3" data-testid="media-upload-video-label">
                <Upload className="w-4 h-4" /> Elegir vídeo de la galería
                <input
                  type="file"
                  accept="video/*"
                  hidden
                  onChange={(e) => handleFileInput(e, "video")}
                  data-testid="media-upload-video"
                />
              </label>
              <p className="text-xs text-faded text-center leading-relaxed">
                Para máxima calidad, configura tu cámara en <strong className="text-gold-light">1080p o 4K</strong> en los ajustes de tu móvil.<br />
                <span className="text-[11px]">Se sube tal cual, sin recompresión · Máx. 500 MB · Máx. 60 min</span>
              </p>
            </div>
          )}
        </div>
      )}

      {/* ============ FILE / IMAGE TAB ============ */}
      {tab === "file" && (
        <div>
          {hasMedia && (media.media_type === "file" || media.media_type === "image") ? (
            <div className="glass-card rounded-md p-4 space-y-3">
              {media.media_type === "image" ? (
                <img src={mediaUrl} alt="" className="w-full rounded-md" />
              ) : (
                <div className="flex items-center gap-3 text-gold-light">
                  <Paperclip className="w-5 h-5" />
                  <a href={mediaUrl} target="_blank" rel="noreferrer" className="truncate hover:text-gold underline">
                    {media.media_filename}
                  </a>
                </div>
              )}
              <div className="flex items-center justify-between text-xs text-faded">
                <span>{fmtBytes(media.media_size)}</span>
                <button type="button" onClick={clearMedia} className="text-faded hover:text-red-400 flex items-center gap-1">
                  <Trash2 className="w-3.5 h-3.5" /> Quitar
                </button>
              </div>
            </div>
          ) : uploadPct !== null ? (
            <div className="glass-card rounded-md p-6 text-center">
              <Loader2 className="w-6 h-6 text-gold animate-spin mx-auto mb-3" />
              <div className="text-sm text-gold-light mb-2">Subiendo… {uploadPct}%</div>
              <div className="h-1.5 bg-obsidian-2 rounded overflow-hidden">
                <div className="h-full bg-gold transition-all duration-150" style={{ width: `${uploadPct}%` }} />
              </div>
            </div>
          ) : (
            <>
              <label className="btn-ghost w-full justify-center cursor-pointer">
                <Upload className="w-4 h-4" /> Seleccionar archivo (imagen, PDF, etc.)
                <input type="file" hidden onChange={(e) => handleFileInput(e)} data-testid="media-upload-file" />
              </label>
              <p className="text-xs text-faded mt-2 text-center">Máximo 500 MB</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
